import {
  ACCOUNT_STATUSES,
  type AccountStatusCode,
  type AccountStatusSnapshot,
  type AccountStatusSnapshotDTO,
} from '@src/models/interfaces/accountStatus.interface';
import { DateUtil } from '@src/packages/utils/dates';

const KNOWN_STATUSES = new Set<string>(Object.values(ACCOUNT_STATUSES));

/** Statuses in which the PWA hides create, update and delete actions. */
const LOCKED_STATUSES = new Set<AccountStatusCode>([
  ACCOUNT_STATUSES.READ_ONLY,
  ACCOUNT_STATUSES.SUSPENDED,
  ACCOUNT_STATUSES.CANCELLED,
]);

const isAccountStatusCode = (value: unknown): value is AccountStatusCode =>
  typeof value === 'string' && KNOWN_STATUSES.has(value);

export const DEFAULT_ACCOUNT_STATUS: AccountStatusSnapshot = {
  accountId: null,
  status: ACCOUNT_STATUSES.TRIALING,
  trialEndsAt: null,
  graceEndsAt: null,
  updatedAt: null,
};

export class AccountStatusMapper {
  /**
   * A missing document means the account was never backfilled: the backend treats it as
   * `trialing`. An unknown status also falls back to `trialing` so a newer backend value
   * cannot lock users out of a client that does not know it.
   */
  static fromDTO(dto: AccountStatusSnapshotDTO | null | undefined): AccountStatusSnapshot {
    if (!dto) {
      return DEFAULT_ACCOUNT_STATUS;
    }

    return {
      accountId: dto.accountId ?? null,
      status: isAccountStatusCode(dto.status) ? dto.status : ACCOUNT_STATUSES.TRIALING,
      trialEndsAt: DateUtil.toDateOrNull(dto.trialEndsAt),
      graceEndsAt: DateUtil.toDateOrNull(dto.graceEndsAt),
      updatedAt: DateUtil.toDateOrNull(dto.updatedAt),
    };
  }

  static isWritable(status: AccountStatusCode): boolean {
    return !LOCKED_STATUSES.has(status);
  }
}
